import { LogOut } from "lucide-react";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { ConfirmModal } from "./ConfirmModal.js";

/**
 * Sign-out control for the enterprise (Entra/dev) edition — used in the app
 * header and the doc editor's account cluster. Owns the logout mutation and a
 * styled confirmation (no accidental sign-out from a stray click). Two visual
 * variants share the same behaviour:
 *   - "text" — header nav link
 *   - "icon" — compact icon button for the doc top bar
 *
 * (b2c sign-out runs through the Clerk UserButton, which has its own confirm.)
 */
export function SignOutButton({ variant }: { variant: "text" | "icon" }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      qc.setQueryData(["me"], null);
      qc.invalidateQueries();
      window.location.href = "/";
    },
  });

  return (
    <>
      {variant === "text" ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="text-white/55 hover:text-white transition-colors duration-150 text-sm"
        >
          Sign out
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="text-white/55 hover:text-white transition-colors duration-150 p-1"
          title="Sign out"
          aria-label="Sign out"
        >
          <LogOut size={16} strokeWidth={1.6} aria-hidden />
        </button>
      )}
      {confirming && (
        <ConfirmModal
          eyebrow="Sign out"
          title="Sign out?"
          body="You'll need to sign in again to get back to your decks."
          confirmLabel="Sign out"
          busyLabel="Signing out…"
          accent="electric"
          busy={logout.isPending}
          error={
            logout.isError ? "Couldn't sign out. Please try again." : null
          }
          onConfirm={() => logout.mutate()}
          onClose={() => setConfirming(false)}
          testId="sign-out-modal"
        />
      )}
    </>
  );
}
